"use client";

import {
	autoUpdate,
	FloatingPortal,
	safePolygon,
	useClick,
	useClientPoint,
	useDismiss,
	useFloating,
	useFocus,
	useHover,
	useInteractions,
	useMergeRefs,
	useRole,
} from "@floating-ui/react";
import {
	motion,
	useMotionValue,
	useReducedMotion,
	useSpring,
	useTransform,
} from "motion/react";
import type {
	FocusEvent,
	ReactElement,
	KeyboardEvent as ReactKeyboardEvent,
	MouseEvent as ReactMouseEvent,
	ReactNode,
	PointerEvent as ReactPointerEvent,
	Ref,
} from "react";
import {
	cloneElement,
	isValidElement,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
	useSyncExternalStore,
} from "react";
import { cn } from "../../../lib/cn";
import {
	ContentTooltipStackedArrow,
	resolveContentTooltipArrowStaticSide,
} from "./content-tooltip-arrow";
import { resolveContentTooltipChrome } from "./content-tooltip-chrome";
import {
	claimContentTooltip,
	getContentTooltipActiveId,
	getContentTooltipActiveInstanceId,
	releaseContentTooltip,
	subscribeContentTooltipActive,
} from "./content-tooltip-exclusive";
import { useContentTooltipGroupId } from "./content-tooltip-group";
import {
	buildContentTooltipMiddleware,
	contentTooltipPlacement,
} from "./content-tooltip-position";
import { CONTENT_TOOLTIP_DEFAULT_OPEN_DELAY_MS } from "./content-tooltip-sizes";
import type {
	ContentTooltipAlign,
	ContentTooltipCollisionPadding,
	ContentTooltipColorScheme,
	ContentTooltipOpenOn,
	ContentTooltipSide,
	ContentTooltipSize,
	ContentTooltipSurface,
	ContentTooltipTransition,
} from "./content-tooltip-types";
import { CONTENT_TOOLTIP_Z_INDEX } from "./content-tooltip-z-index";
import { useResolvedContentTooltipOpenOn } from "./use-content-tooltip-open";

const UNMOUNT_MS = 220;
const DEFAULT_CLOSE_DELAY_MS = 80;
const ARM_MOVE_TOLERANCE_PX = 10;

export interface ContentTooltipProps {
	children: ReactNode;
	content?: ReactNode;
	side?: ContentTooltipSide;
	align?: ContentTooltipAlign;
	sideOffset?: number;
	collisionPadding?: ContentTooltipCollisionPadding;
	/** Default true: keep tip in viewport (autoPlacement/flip + shift). */
	autoPlace?: boolean;
	lazyMount?: boolean;
	/** Open delay ms — default CONTENT_TOOLTIP_DEFAULT_OPEN_DELAY_MS (0). */
	delayDuration?: number;
	closeDelay?: number;
	interactive?: boolean;
	dismissOnTriggerLeave?: boolean;
	followCursor?: boolean;
	/**
	 * Show shadcn-core arrow. Default: `!followCursor`
	 * (on when anchored; off when cursor-follow).
	 */
	showArrow?: boolean;
	openOn?: ContentTooltipOpenOn;
	size?: ContentTooltipSize;
	surface?: ContentTooltipSurface;
	/** Default `system` (light twin + ability dark SoT). */
	colorScheme?: ContentTooltipColorScheme;
	transition?: ContentTooltipTransition;
	contentClassName?: string;
	triggerClassName?: string;
	/**
	 * Merge reference props onto the single child (Radix `TooltipTrigger asChild` parity).
	 * Prefer when the trigger must participate in parent flex/grid without an extra wrapper.
	 */
	asChild?: boolean;
	disabled?: boolean;
	groupId?: string;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
}

function transitionClass(
	transition: ContentTooltipTransition,
	reducedMotion: boolean,
): string {
	if (reducedMotion) {
		return "transition-opacity duration-200 ease-out";
	}
	switch (transition) {
		case "fade":
			return "transition-opacity duration-200 ease-out";
		case "fadeZoom":
			// Radix leftover parity: fade + zoom 95↔100 open/close.
			return "origin-center transition-[opacity,transform] duration-200 ease-out will-change-[opacity,transform] data-[state=closed]:scale-95 data-[state=open]:scale-100";
		case "morph":
			return "transition-[opacity,transform] duration-200 ease-out";
		case "springTilt":
			return "transition-opacity duration-200 ease-out will-change-transform";
	}
}

/**
 * Prefer-over ContentTooltip — stable host, exclusive singleton portal,
 * Floating UI autoPlace / followCursor / safePolygon / arrow, z-index 320.
 */
export function ContentTooltip({
	children,
	content,
	side,
	align,
	sideOffset = 10,
	collisionPadding,
	autoPlace = true,
	lazyMount = true,
	delayDuration = CONTENT_TOOLTIP_DEFAULT_OPEN_DELAY_MS,
	closeDelay = DEFAULT_CLOSE_DELAY_MS,
	interactive = false,
	dismissOnTriggerLeave = true,
	followCursor = false,
	showArrow: showArrowProp,
	openOn,
	size = "md",
	surface = "default",
	colorScheme = "system",
	transition = "fadeZoom",
	contentClassName,
	triggerClassName,
	asChild = false,
	disabled = false,
	groupId: groupIdProp,
	open: openControlled,
	onOpenChange,
}: ContentTooltipProps) {
	const reactId = useId();
	const groupFromContext = useContentTooltipGroupId();
	const exclusiveId = groupIdProp ?? groupFromContext ?? reactId;
	/** Instance within a shared morph group; equals exclusiveId when ungrouped. */
	const instanceId = reactId;

	const showArrowEffective = showArrowProp ?? !followCursor;

	const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
	const isControlled = openControlled !== undefined;
	const open = isControlled ? Boolean(openControlled) : uncontrolledOpen;

	const setOpen = useCallback(
		(next: boolean) => {
			if (disabled && next) {
				return;
			}
			// Controlled: parent owns open + exclusive claim (via open-prop effect).
			// Floating UI must not release the slot while `open` stays true.
			if (isControlled) {
				onOpenChange?.(next);
				return;
			}
			if (next) {
				claimContentTooltip(exclusiveId, instanceId);
			} else {
				releaseContentTooltip(exclusiveId, instanceId);
			}
			setUncontrolledOpen(next);
			onOpenChange?.(next);
		},
		[disabled, exclusiveId, instanceId, isControlled, onOpenChange],
	);

	const activeId = useSyncExternalStore(
		subscribeContentTooltipActive,
		getContentTooltipActiveId,
		() => null,
	);
	const activeInstanceId = useSyncExternalStore(
		subscribeContentTooltipActive,
		getContentTooltipActiveInstanceId,
		() => null,
	);
	const isExclusiveOwner =
		activeId === exclusiveId && activeInstanceId === instanceId;
	const showPortal = open && isExclusiveOwner && !disabled && content != null;

	const [mounted, setMounted] = useState(() => !lazyMount);
	/** True after first paint in closed state — drives CSS enter transition. */
	const [entered, setEntered] = useState(false);
	const unmountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const enterFrameRef = useRef<number | null>(null);
	const wasExclusiveOwnerRef = useRef(false);
	const arrowRef = useRef<HTMLDivElement | null>(null);
	const reducedMotion = useReducedMotion() ?? false;
	const openPolicy = useResolvedContentTooltipOpenOn(openOn);

	const clearUnmountTimer = useCallback(() => {
		if (unmountTimerRef.current != null) {
			clearTimeout(unmountTimerRef.current);
			unmountTimerRef.current = null;
		}
	}, []);

	const clearEnterFrame = useCallback(() => {
		if (enterFrameRef.current != null) {
			cancelAnimationFrame(enterFrameRef.current);
			enterFrameRef.current = null;
		}
	}, []);

	// Claim / release follows visible open (controlled + uncontrolled).
	useEffect(() => {
		if (open && !disabled && content != null) {
			claimContentTooltip(exclusiveId, instanceId);
			return;
		}
		releaseContentTooltip(exclusiveId, instanceId);
	}, [open, disabled, content, exclusiveId, instanceId]);

	// Close only after we owned the slot and another tip stole it / exclusive cleared.
	useEffect(() => {
		if (wasExclusiveOwnerRef.current && open && !isExclusiveOwner) {
			if (!isControlled) {
				setUncontrolledOpen(false);
			}
			onOpenChange?.(false);
		}
		wasExclusiveOwnerRef.current = isExclusiveOwner;
	}, [isExclusiveOwner, open, isControlled, onOpenChange]);

	useEffect(() => {
		if (showPortal) {
			clearUnmountTimer();
			setMounted(true);
			// Paint closed (opacity 0 / scale 95), then open on next frames so CSS transitions run.
			clearEnterFrame();
			setEntered(false);
			enterFrameRef.current = requestAnimationFrame(() => {
				enterFrameRef.current = requestAnimationFrame(() => {
					enterFrameRef.current = null;
					setEntered(true);
				});
			});
			return () => {
				clearEnterFrame();
			};
		}
		setEntered(false);
		if (!lazyMount) {
			return;
		}
		clearUnmountTimer();
		unmountTimerRef.current = setTimeout(() => {
			unmountTimerRef.current = null;
			setMounted(false);
		}, UNMOUNT_MS);
		return () => {
			clearUnmountTimer();
		};
	}, [showPortal, lazyMount, clearUnmountTimer, clearEnterFrame]);

	useEffect(() => {
		return () => {
			clearUnmountTimer();
			clearEnterFrame();
			releaseContentTooltip(exclusiveId, instanceId);
		};
	}, [clearUnmountTimer, clearEnterFrame, exclusiveId, instanceId]);

	const visuallyOpen = showPortal && entered;
	const middleware = useMemo(
		() =>
			buildContentTooltipMiddleware({
				side,
				align,
				sideOffset,
				collisionPadding,
				autoPlace,
				followCursor,
				arrowRef,
				showArrow: showArrowEffective,
			}),
		[
			side,
			align,
			sideOffset,
			collisionPadding,
			autoPlace,
			followCursor,
			showArrowEffective,
		],
	);

	const placement = contentTooltipPlacement(side, align);

	const {
		refs,
		floatingStyles,
		context,
		middlewareData,
		placement: resolvedPlacement,
	} = useFloating({
		open: showPortal,
		onOpenChange: (next) => {
			setOpen(next);
		},
		placement,
		whileElementsMounted: autoUpdate,
		middleware,
	});

	const clientPoint = useClientPoint(context, {
		enabled: Boolean(followCursor && !disabled),
	});

	const useSafePolygon = interactive && !dismissOnTriggerLeave;

	const hover = useHover(context, {
		enabled: openPolicy.enableHover && !disabled,
		delay: { open: delayDuration, close: closeDelay },
		handleClose: useSafePolygon
			? safePolygon({ requireIntent: false })
			: null,
		move: followCursor,
	});

	const focus = useFocus(context, {
		enabled: !disabled && openPolicy.enableHover,
	});

	const click = useClick(context, {
		enabled: openPolicy.enableClick && !disabled,
		toggle: true,
	});

	const dismiss = useDismiss(context, {
		enabled: !disabled,
		escapeKey: true,
		outsidePress: true,
	});

	const role = useRole(context, { role: "tooltip" });

	const { getReferenceProps, getFloatingProps } = useInteractions([
		clientPoint,
		hover,
		focus,
		click,
		dismiss,
		role,
	]);

	const chrome = resolveContentTooltipChrome({
		surface,
		colorScheme,
		size,
	});

	const panelState = visuallyOpen ? "open" : "closed";
	const springTilt = useSpringTiltFollow({
		enabled: transition === "springTilt" && !reducedMotion,
	});

	const pointerArmRef = useRef<{ x: number; y: number } | null>(null);

	const onPointerDownCapture = useCallback((event: ReactPointerEvent) => {
		pointerArmRef.current = { x: event.clientX, y: event.clientY };
	}, []);

	const onPointerUpCapture = useCallback(
		(event: ReactPointerEvent) => {
			const start = pointerArmRef.current;
			pointerArmRef.current = null;
			if (!start || !openPolicy.enableClick) {
				return;
			}
			const dx = event.clientX - start.x;
			const dy = event.clientY - start.y;
			if (Math.hypot(dx, dy) > ARM_MOVE_TOLERANCE_PX) {
				event.preventDefault();
			}
		},
		[openPolicy.enableClick],
	);

	const handleBlurCapture = useCallback(
		(event: FocusEvent<HTMLDivElement>) => {
			const next = event.relatedTarget;
			if (next instanceof Node && event.currentTarget.contains(next)) {
				return;
			}
			if (useSafePolygon) {
				return;
			}
			setOpen(false);
		},
		[setOpen, useSafePolygon],
	);

	const asChildElement =
		asChild && isValidElement(children)
			? (children as ReactElement<{
					className?: string;
					ref?: Ref<HTMLElement>;
					onClick?: (event: ReactMouseEvent) => void;
					onPointerDown?: (event: ReactPointerEvent) => void;
					onPointerUp?: (event: ReactPointerEvent) => void;
					onPointerMove?: (event: ReactPointerEvent) => void;
					onPointerLeave?: (event: ReactPointerEvent) => void;
					onFocus?: (event: FocusEvent) => void;
					onBlur?: (event: FocusEvent) => void;
					onKeyDown?: (event: ReactKeyboardEvent) => void;
				}>)
			: null;
	const childRef = asChildElement != null ? asChildElement.props.ref : null;
	const referenceRef = useMergeRefs([refs.setReference, childRef]);

	/**
	 * Pass user handlers into `getReferenceProps` so Floating UI composes them
	 * (asChild must not overwrite button `onClick` — inventory/shop wallet links).
	 * Click while open always dismisses (actionable chrome / header CircleButtons).
	 */
	const referenceProps = getReferenceProps({
		onPointerDownCapture,
		onPointerUpCapture,
		onBlurCapture: handleBlurCapture,
		onPointerMove: (event: ReactPointerEvent) => {
			springTilt.onPointerMove(event);
			asChildElement?.props.onPointerMove?.(event);
		},
		onPointerLeave: (event: ReactPointerEvent) => {
			springTilt.onPointerLeave();
			asChildElement?.props.onPointerLeave?.(event);
		},
		onClick: (event: ReactMouseEvent) => {
			asChildElement?.props.onClick?.(event);
			if (open) {
				setOpen(false);
			}
		},
		...(asChildElement != null
			? {
					onPointerDown: asChildElement.props.onPointerDown,
					onPointerUp: asChildElement.props.onPointerUp,
					onFocus: asChildElement.props.onFocus,
					onBlur: asChildElement.props.onBlur,
					onKeyDown: asChildElement.props.onKeyDown,
				}
			: null),
	});

	const arrowData = middlewareData.arrow;
	const arrowStaticSide =
		resolveContentTooltipArrowStaticSide(resolvedPlacement);

	const floatingPortal =
		mounted && content != null ? (
			<FloatingPortal>
				<div
					ref={refs.setFloating}
					style={{
						...floatingStyles,
						zIndex: CONTENT_TOOLTIP_Z_INDEX,
					}}
					className={cn(!visuallyOpen && "pointer-events-none")}
					data-content-tooltip-floating=""
					{...getFloatingProps()}
				>
					{springTilt.enabled ? (
						<motion.div
							className={cn(
								"relative",
								chrome,
								transitionClass(transition, reducedMotion),
								visuallyOpen ? "opacity-100" : "opacity-0",
								contentClassName,
							)}
							data-content-tooltip-content=""
							data-state={panelState}
							data-color-scheme={colorScheme}
							data-show-arrow={
								showArrowEffective ? "true" : "false"
							}
							data-spring-tilt=""
							style={springTilt.style}
						>
							<div className="relative z-10">{content}</div>
							{showArrowEffective ? (
								<ContentTooltipStackedArrow
									arrowRef={arrowRef}
									staticSide={arrowStaticSide}
									arrowX={arrowData?.x}
									arrowY={arrowData?.y}
									colorScheme={colorScheme}
								/>
							) : null}
						</motion.div>
					) : (
						<div
							className={cn(
								"relative",
								chrome,
								transitionClass(transition, reducedMotion),
								visuallyOpen ? "opacity-100" : "opacity-0",
								contentClassName,
							)}
							data-content-tooltip-content=""
							data-state={panelState}
							data-color-scheme={colorScheme}
							data-show-arrow={
								showArrowEffective ? "true" : "false"
							}
						>
							<div className="relative z-10">{content}</div>
							{showArrowEffective ? (
								<ContentTooltipStackedArrow
									arrowRef={arrowRef}
									staticSide={arrowStaticSide}
									arrowX={arrowData?.x}
									arrowY={arrowData?.y}
									colorScheme={colorScheme}
								/>
							) : null}
						</div>
					)}
				</div>
			</FloatingPortal>
		) : null;

	if (asChildElement != null) {
		return (
			<>
				{cloneElement(asChildElement, {
					...referenceProps,
					ref: referenceRef,
					className: cn(
						asChildElement.props.className,
						triggerClassName,
					),
					"data-content-tooltip": "",
					"data-content-tooltip-id": exclusiveId,
				} as never)}
				{floatingPortal}
			</>
		);
	}

	return (
		<div
			ref={referenceRef}
			className={cn("relative", triggerClassName)}
			data-content-tooltip=""
			data-content-tooltip-id={exclusiveId}
			{...referenceProps}
		>
			{children}
			{floatingPortal}
		</div>
	);
}

/**
 * Loot / AnimatedTooltip curved follow — springy `translateX` + `rotate` from
 * pointer offset within the **trigger** (shadcn-space tooltip-03 parity).
 * Paired with `followCursor` so the tip tracks the pointer while the panel sways.
 */
function useSpringTiltFollow(params: { enabled: boolean }) {
	const { enabled } = params;
	const x = useMotionValue(0);
	const rotate = useSpring(useTransform(x, [-100, 100], [-14, 14]), {
		stiffness: 100,
		damping: 15,
	});
	const translateX = useSpring(useTransform(x, [-100, 100], [-20, 20]), {
		stiffness: 100,
		damping: 15,
	});

	const onPointerMove = useCallback(
		(event: ReactPointerEvent) => {
			if (!enabled) {
				return;
			}
			const target = event.currentTarget;
			if (!(target instanceof Element)) {
				return;
			}
			const rect = target.getBoundingClientRect();
			x.set(event.clientX - rect.left - rect.width / 2);
		},
		[enabled, x],
	);

	const onPointerLeave = useCallback(() => {
		x.set(0);
	}, [x]);

	useEffect(() => {
		if (!enabled) {
			x.set(0);
		}
	}, [enabled, x]);

	return {
		enabled,
		style: enabled ? { translateX, rotate } : undefined,
		onPointerMove,
		onPointerLeave,
	};
}
